import {
  ordersService,
  type OrderDto,
  type OrdersListFilterMeta,
  type OrdersListParams,
  type OrdersListStats,
} from '@/services/orders.service';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface OrdersState {
  orders: OrderDto[];
  total: number;
  page: number;
  limit: number;
  stats: OrdersListStats | null;
  filterMeta: OrdersListFilterMeta | null;
  isLoading: boolean;
  error: string | null;
  setOrders: (orders: OrderDto[]) => void;
  setStats: (stats: OrdersListStats | null) => void;
  setFilterMeta: (meta: OrdersListFilterMeta | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  fetchOrders: (params?: OrdersListParams) => Promise<void>;
}

export const useOrdersStore = create<OrdersState>()(
  devtools(
    (set) => ({
      orders: [],
      total: 0,
      page: 1,
      limit: 100,
      stats: null,
      filterMeta: null,
      isLoading: false,
      error: null,
      setOrders: (orders) => {
        set({ orders }, undefined, 'orders/setOrders');
      },
      setStats: (stats) => {
        set({ stats }, undefined, 'orders/setStats');
      },
      setFilterMeta: (filterMeta) => {
        set({ filterMeta }, undefined, 'orders/setFilterMeta');
      },
      setLoading: (loading) => {
        set({ isLoading: loading }, undefined, 'orders/setLoading');
      },
      setError: (error) => {
        set({ error }, undefined, 'orders/setError');
      },
      fetchOrders: async (params) => {
        set({ isLoading: true, error: null }, undefined, 'orders/fetchStart');

        try {
          const response = await ordersService.list(params);
          set(
            {
              orders: response.items,
              total: response.total,
              page: response.page,
              limit: response.limit,
              stats: response.stats,
              filterMeta: response.filterMeta,
            },
            undefined,
            'orders/fetchSuccess'
          );
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Failed to load orders';
          set({ error: message }, undefined, 'orders/fetchError');
        } finally {
          set({ isLoading: false }, undefined, 'orders/fetchEnd');
        }
      },
    }),
    { name: 'orders-store' }
  )
);
